import { useState, useRef, useCallback, useEffect } from 'react';
import { Box, alpha } from '@mui/material';
import { CRM_PALETTE as P } from '../../theme/brandPalette';
import { watchScroller } from './watchScroller';

/**
 * Barra de scroll SIEMPRE visible y arrastrable (mouse/touchpad).
 *
 * Las scrollbars nativas (::-webkit-scrollbar) no son confiables cross-browser:
 * en Safari / macOS con "mostrar barras al desplazarse" se desvanecen igual (overlay),
 * así que solo se ven MIENTRAS se arrastra. Esta barra la dibujamos nosotros: visible
 * apenas el contenido desborda, independiente del SO y del navegador. (reportado por Evelyn)
 *
 * Uso: montar dentro de un contenedor `position:relative` que envuelva al scroller,
 * ocultando la barra nativa del scroller (`scrollbarWidth:'none'` + `::-webkit-scrollbar
 * { display:none }`) y pasando su ref:
 *
 *   <Box sx={{ position:'relative' }}>
 *     <Box ref={scrollRef} sx={{ overflowY:'auto', scrollbarWidth:'none', '&::-webkit-scrollbar':{ display:'none' } }}>…</Box>
 *     <AlwaysScrollbar scrollRef={scrollRef} />
 *   </Box>
 */
export function AlwaysScrollbar({ scrollRef }: { scrollRef: React.RefObject<HTMLDivElement | null> }) {
  const [m, setM] = useState({ thumbH: 0, thumbTop: 0, visible: false });
  const drag = useRef<{ startY: number; startScroll: number } | null>(null);

  const recompute = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollHeight, clientHeight, scrollTop } = el;
    if (scrollHeight <= clientHeight + 1) {
      setM((prev) => (prev.visible ? { ...prev, visible: false } : prev));
      return;
    }
    const trackH = clientHeight;
    const thumbH = Math.max(36, (clientHeight / scrollHeight) * trackH);
    const maxTop = trackH - thumbH;
    const thumbTop = ((scrollTop / (scrollHeight - clientHeight)) || 0) * maxTop;
    setM({ thumbH, thumbTop, visible: true });
  }, [scrollRef]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Ver watchScroller: el contenido de estos scrollers crece después de montar y
    // escuchar solo scroll/resize dejaba la barra oculta con el contenido desbordado.
    return watchScroller(el, recompute);
  }, [recompute, scrollRef]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const d = drag.current; const el = scrollRef.current;
      if (!d || !el) return;
      const trackH = el.clientHeight;
      const thumbH = Math.max(36, (el.clientHeight / el.scrollHeight) * trackH);
      const maxTop = trackH - thumbH;
      const range = el.scrollHeight - el.clientHeight;
      el.scrollTop = d.startScroll + ((e.clientY - d.startY) / maxTop) * range;
    };
    const onUp = () => { if (drag.current) { drag.current = null; document.body.style.userSelect = ''; } };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [scrollRef]);

  if (!m.visible) return null;
  return (
    <Box sx={{ position: 'absolute', top: 6, right: 3, bottom: 6, width: 12, zIndex: 3, pointerEvents: 'none' }}>
      <Box
        onMouseDown={(e) => {
          const el = scrollRef.current; if (!el) return;
          drag.current = { startY: e.clientY, startScroll: el.scrollTop };
          document.body.style.userSelect = 'none';
          e.preventDefault();
        }}
        sx={{
          position: 'absolute', right: 0, width: 9, borderRadius: 5,
          top: m.thumbTop, height: m.thumbH,
          bgcolor: alpha(P.brand.golden, 0.6),
          '&:hover': { bgcolor: P.brand.golden, width: 11 },
          cursor: 'grab', pointerEvents: 'auto',
          transition: 'background 0.15s ease, width 0.12s ease',
        }}
      />
    </Box>
  );
}

export default AlwaysScrollbar;
